/**
 * Main Application Store — organized in slices
 *
 * Slices: chatbot, conversation, document, chat, ui
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Chatbot,
  ChatbotCreate,
  ChatbotUpdate,
  Conversation,
  ConversationChatRequest,
  Document,
  Message,
  ChatRequest,
  SourceDocument,
  ElicitationInterrupt,
  ElicitationResponse,
  Reservation,
  AppInfoResponse,
} from '@/types/api';
import apiClient, { ChatStreamEvent } from '@/services/api';

interface AppState {
  // --- Chatbot slice ---
  currentChatbot: Chatbot | null;
  chatbots: Chatbot[];
  isLoadingChatbots: boolean;

  loadChatbots: () => Promise<void>;
  createChatbot: (chatbot: ChatbotCreate) => Promise<Chatbot>;
  updateChatbot: (id: string, updates: ChatbotUpdate) => Promise<Chatbot>;
  deleteChatbot: (id: string) => Promise<void>;
  selectChatbot: (chatbot: Chatbot | null) => void;
  clearChatbotData: (id: string) => Promise<void>;

  // --- Conversation slice ---
  currentConversation: Conversation | null;
  conversations: Conversation[];
  isLoadingConversations: boolean;

  loadConversations: (chatbotId?: string) => Promise<void>;
  createConversation: (chatbotId: string, title?: string) => Promise<Conversation>;
  selectConversation: (conversation: Conversation | null) => void;
  renameConversation: (id: string, title: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;

  // --- Document slice ---
  documents: Document[];
  isLoadingDocuments: boolean;
  isUploading: boolean;
  uploadError: string | null;

  loadDocuments: (conversationId: string) => Promise<void>;
  uploadLocalFiles: (conversationId: string, files: FileList) => Promise<Document[]>;
  deleteDocument: (id: string) => Promise<void>;

  // --- Chat slice ---
  messages: Message[];
  isLoadingMessages: boolean;
  isStreaming: boolean;
  isClarifying: boolean;
  /** Set while the agent waits for the user's answer to an MCP confirmation. */
  pendingInterrupt: ElicitationInterrupt | null;
  chatError: string | null;
  /** Steps of the answer being prepared (« Recherche sur le site… »), until its first word */
  progress: string[];

  loadConversationMessages: (conversationId: string) => Promise<void>;
  sendMessageStream: (conversationId: string, message: string) => Promise<void>;
  /** Answer the pending confirmation and stream the rest of the agent's turn. */
  resolveInterrupt: (responses: Record<string, ElicitationResponse>) => Promise<void>;
  clearChatHistory: (chatbotId: string) => Promise<void>;

  // --- App config (GET /api/info) ---
  appInfo: AppInfoResponse | null;
  loadAppInfo: () => Promise<void>;

  // --- UI slice ---
  sidebarOpen: boolean;
  error: string | null;
  isLoading: boolean;

  toggleSidebar: () => void;
  clearError: () => void;
  reset: () => void;
}

type Setter = (partial: Partial<AppState>) => void;
type Getter = () => AppState;

/**
 * Drain a chat SSE stream into the store: tokens grow the assistant message,
 * metadata events (sources, reservation card, pending confirmation) attach to it.
 */
async function consumeChatStream(
  stream: AsyncGenerator<ChatStreamEvent, void, unknown>,
  chatbotId: string,
  set: Setter,
  get: Getter
): Promise<void> {
  let assistantResponse = '';
  let assistantMessage: Message | null = null;
  let sources: SourceDocument[] | undefined;
  let reservation: Reservation | undefined;
  let interrupt: ElicitationInterrupt | undefined;

  const upsertAssistant = () => {
    if (!assistantMessage) {
      assistantMessage = {
        id: `stream-${Date.now()}`,
        chatbot_id: chatbotId,
        role: 'assistant',
        content: assistantResponse,
        timestamp: new Date().toISOString(),
      };
      set({ messages: [...get().messages, assistantMessage] });
    } else {
      assistantMessage.content = assistantResponse;
      set({
        messages: get().messages.map((m) =>
          m.id === assistantMessage!.id ? { ...assistantMessage! } : m
        ),
      });
    }
  };

  try {
    for await (const chunk of stream) {
      if (typeof chunk === 'string') {
        assistantResponse += chunk;
        upsertAssistant();
        continue;
      }
      if ('status' in chunk) {
        const { progress } = get();
        if (progress[progress.length - 1] !== chunk.status) set({ progress: [...progress, chunk.status] });
      } else if ('replace' in chunk) {
        assistantResponse = chunk.replace;
        upsertAssistant();
      } else if ('sources' in chunk) {
        sources = chunk.sources;
      } else if ('reservation' in chunk) {
        reservation = chunk.reservation;
      } else if ('interrupt' in chunk) {
        interrupt = chunk.interrupt;
      } else if ('clarification' in chunk) {
        set({ isClarifying: true });
      } else if ('conv' in chunk) {
        // Conversation metadata (auto-created conversation)
        const convData = chunk.conv;
        const { currentConversation, conversations } = get();
        if (!currentConversation || currentConversation.id !== convData.id) {
          const newConv: Conversation = {
            id: convData.id,
            chatbot_id: chatbotId,
            title: convData.title,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          set({
            currentConversation: newConv,
            conversations: [newConv, ...conversations],
          });
        } else if (currentConversation.title !== convData.title) {
          // The backend named the conversation after its first exchange
          set({
            currentConversation: { ...currentConversation, title: convData.title },
            conversations: conversations.map((c) =>
              c.id === convData.id ? { ...c, title: convData.title } : c
            ),
          });
        }
      }
    }

    // A confirmation request with no text: show the server's question as the message
    if (interrupt && !assistantResponse) {
      assistantResponse = interrupt.requests.map((r) => r.message).join('\n');
      upsertAssistant();
    }

    if (assistantMessage && (sources || reservation)) {
      const msg = assistantMessage as Message;
      if (sources) msg.source_documents = sources;
      if (reservation) msg.reservation = reservation;
      set({
        messages: get().messages.map((m) => (m.id === msg.id ? { ...msg } : m)),
      });
    }

    // Update conversation's updated_at locally for sort order
    const { currentConversation, conversations } = get();
    if (currentConversation) {
      const now = new Date().toISOString();
      set({
        currentConversation: { ...currentConversation, updated_at: now },
        conversations: conversations.map((c) =>
          c.id === currentConversation.id ? { ...c, updated_at: now } : c
        ),
      });
    }

    set({ isStreaming: false, pendingInterrupt: interrupt ?? null, progress: [] });
  } catch (error: any) {
    set({ chatError: error.error || error.message || 'Failed to stream message', isStreaming: false, progress: [] });
    throw error;
  }
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // ==================== Chatbot Slice ====================
      currentChatbot: null,
      chatbots: [],
      isLoadingChatbots: false,

      loadChatbots: async () => {
        set({ isLoadingChatbots: true, error: null });
        try {
          const response = await apiClient.getChatbots({ limit: 100 });
          set({ chatbots: response.items, isLoadingChatbots: false });
        } catch (error: any) {
          set({ error: error.error || 'Failed to load chatbots', isLoadingChatbots: false });
        }
      },

      createChatbot: async (chatbot: ChatbotCreate) => {
        set({ isLoading: true, error: null });
        try {
          const newChatbot = await apiClient.createChatbot(chatbot);
          const { chatbots } = get();
          set({
            chatbots: [newChatbot, ...chatbots],
            currentChatbot: newChatbot,
            isLoading: false,
          });
          return newChatbot;
        } catch (error: any) {
          set({ error: error.error || 'Failed to create chatbot', isLoading: false });
          throw error;
        }
      },

      updateChatbot: async (id: string, updates: ChatbotUpdate) => {
        set({ isLoading: true, error: null });
        try {
          const updatedChatbot = await apiClient.updateChatbot(id, updates);
          const { chatbots, currentChatbot } = get();
          set({
            chatbots: chatbots.map((b) => (b.id === id ? updatedChatbot : b)),
            currentChatbot: currentChatbot?.id === id ? updatedChatbot : currentChatbot,
            isLoading: false,
          });
          return updatedChatbot;
        } catch (error: any) {
          set({ error: error.error || 'Failed to update chatbot', isLoading: false });
          throw error;
        }
      },

      deleteChatbot: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.deleteChatbot(id);
          const { chatbots, currentChatbot } = get();
          const isSelected = currentChatbot?.id === id;
          set({
            chatbots: chatbots.filter((b) => b.id !== id),
            currentChatbot: isSelected ? null : currentChatbot,
            conversations: isSelected ? [] : get().conversations,
            currentConversation: isSelected ? null : get().currentConversation,
            documents: isSelected ? [] : get().documents,
            messages: isSelected ? [] : get().messages,
            isLoading: false,
          });
        } catch (error: any) {
          set({ error: error.error || 'Failed to delete chatbot', isLoading: false });
          throw error;
        }
      },

      selectChatbot: (chatbot: Chatbot | null) => {
        set({
          currentChatbot: chatbot,
          currentConversation: null,
          conversations: [],
          documents: [],
          messages: [],
        });
      },

      clearChatbotData: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const clearedChatbot = await apiClient.clearChatbotData(id);
          const { chatbots, currentChatbot } = get();
          set({
            chatbots: chatbots.map((b) => (b.id === id ? clearedChatbot : b)),
            currentChatbot: currentChatbot?.id === id ? clearedChatbot : currentChatbot,
            conversations: [],
            currentConversation: null,
            documents: [],
            messages: [],
            isLoading: false,
          });
        } catch (error: any) {
          set({ error: error.error || 'Failed to clear chatbot data', isLoading: false });
          throw error;
        }
      },

      // ==================== Conversation Slice ====================
      currentConversation: null,
      conversations: [],
      isLoadingConversations: false,

      loadConversations: async (chatbotId?: string) => {
        set({ isLoadingConversations: true });
        try {
          const response = await apiClient.getConversations(chatbotId, { limit: 100 });
          set({ conversations: response.items, isLoadingConversations: false });
        } catch (error: any) {
          set({ error: error.error || 'Failed to load conversations', isLoadingConversations: false });
        }
      },

      createConversation: async (chatbotId: string, title?: string) => {
        try {
          const conv = await apiClient.createConversation(chatbotId, { title });
          const { conversations } = get();
          set({
            conversations: [conv, ...conversations],
            currentConversation: conv,
            messages: [],
            documents: [],
          });
          return conv;
        } catch (error: any) {
          set({ error: error.error || 'Failed to create conversation' });
          throw error;
        }
      },

      selectConversation: (conversation: Conversation | null) => {
        // Documents are scoped per conversation — clear; Dashboard loads them for the new one
        set({ currentConversation: conversation, messages: [], isClarifying: false, pendingInterrupt: null, documents: [] });
      },

      renameConversation: async (id: string, title: string) => {
        try {
          const updated = await apiClient.renameConversation(id, { title });
          const { conversations, currentConversation } = get();
          set({
            conversations: conversations.map((c) => (c.id === id ? updated : c)),
            currentConversation: currentConversation?.id === id ? updated : currentConversation,
          });
        } catch (error: any) {
          set({ error: error.error || 'Failed to rename conversation' });
        }
      },

      deleteConversation: async (id: string) => {
        try {
          await apiClient.deleteConversation(id);
          const { conversations, currentConversation } = get();
          const isSelected = currentConversation?.id === id;
          set({
            conversations: conversations.filter((c) => c.id !== id),
            currentConversation: isSelected ? null : currentConversation,
            messages: isSelected ? [] : get().messages,
          });
        } catch (error: any) {
          set({ error: error.error || 'Failed to delete conversation' });
        }
      },

      // ==================== Document Slice ====================
      documents: [],
      isLoadingDocuments: false,
      isUploading: false,
      uploadError: null,

      loadDocuments: async (conversationId: string) => {
        set({ isLoadingDocuments: true, error: null });
        try {
          const response = await apiClient.getDocuments(conversationId, { limit: 100 });
          set({ documents: response.items, isLoadingDocuments: false });
        } catch (error: any) {
          set({ error: error.error || 'Failed to load documents', isLoadingDocuments: false });
        }
      },

      uploadLocalFiles: async (conversationId: string, files: FileList) => {
        set({ isUploading: true, uploadError: null });
        try {
          const newDocs = await apiClient.uploadLocalFiles(conversationId, files);
          const { documents } = get();
          set({ documents: [...documents, ...newDocs], isUploading: false });
          return newDocs;
        } catch (error: any) {
          set({ uploadError: error.error || 'Failed to upload files', isUploading: false });
          throw error;
        }
      },

      deleteDocument: async (id: string) => {
        set({ isLoadingDocuments: true, error: null });
        try {
          await apiClient.deleteDocument(id);
          const { documents } = get();
          set({ documents: documents.filter((d) => d.id !== id), isLoadingDocuments: false });
        } catch (error: any) {
          set({ error: error.error || 'Failed to delete document', isLoadingDocuments: false });
          throw error;
        }
      },

      // ==================== Chat Slice ====================
      messages: [],
      isLoadingMessages: false,
      isStreaming: false,
      isClarifying: false,
      pendingInterrupt: null,
      chatError: null,
      progress: [],

      loadConversationMessages: async (conversationId: string) => {
        set({ isLoadingMessages: true, chatError: null });
        try {
          const response = await apiClient.getConversationMessages(conversationId, { limit: 100 });
          set({ messages: response.items, isLoadingMessages: false });
        } catch (error: any) {
          set({ chatError: error.error || 'Failed to load messages', isLoadingMessages: false });
        }
      },

      sendMessageStream: async (conversationId: string, message: string) => {
        set({ isStreaming: true, chatError: null, isClarifying: false, pendingInterrupt: null, progress: [] });

        const { currentChatbot } = get();
        const chatbotId = currentChatbot?.id || '';

        // Add user message optimistically
        const userMessage: Message = {
          id: `temp-${Date.now()}`,
          chatbot_id: chatbotId,
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
        };
        set({ messages: [...get().messages, userMessage] });

        await consumeChatStream(
          apiClient.conversationChatStream(conversationId, { message }),
          chatbotId,
          set,
          get
        );
      },

      resolveInterrupt: async (responses: Record<string, ElicitationResponse>) => {
        const { currentConversation, currentChatbot, pendingInterrupt } = get();
        if (!currentConversation || !pendingInterrupt) return;
        const chatbotId = currentChatbot?.id || '';

        const accepted = Object.values(responses).every(
          (r) => r.action === 'accept' && (r.content?.confirme ?? true) !== false
        );
        // A form answer (anything beyond the confirm flag) is summarised with its labels
        const isForm = Object.values(responses).some(
          (r) => r.content && Object.keys(r.content).some((k) => k !== 'confirme')
        );
        const isFormDecline = !isForm && Object.keys(responses).every((k) => k === 'order_details');
        let content = accepted ? 'Je confirme la réservation.' : "J'annule la réservation.";
        if (isForm) {
          // Short summary: the pass, the date, the skier — the recap card carries the rest
          const parts: string[] = [];
          for (const req of pendingInterrupt.requests) {
            const c = responses[req.key]?.content ?? {};
            const props = req.requested_schema?.properties ?? {};
            for (const k of ['item_id', 'start_date', 'skier_first_name', 'skier_last_name']) {
              const v = c[k];
              if (v === null || v === '' || v === undefined) continue;
              const def = props[k];
              const idx = def?.enum?.indexOf(String(v)) ?? -1;
              if (idx >= 0) parts.push((def?.enumNames?.[idx] ?? String(v)).replace(/\s*\(.*?\)\s*$/, ''));
              else if (def?.format === 'date') parts.push(new Date(`${v}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }));
              else parts.push(String(v));
            }
          }
          content = parts.length ? `Formulaire complété · ${parts.join(' · ')}` : 'Formulaire complété';
        } else if (isFormDecline && !accepted) {
          content = 'Je préfère répondre par écrit.';
        } else if (!accepted && Object.values(responses).some((r) => r.action === 'decline')) {
          content = 'Je souhaite modifier ma commande.';
        }
        set({ isStreaming: true, chatError: null, pendingInterrupt: null, progress: [] });

        const userMessage: Message = {
          id: `temp-${Date.now()}`,
          chatbot_id: chatbotId,
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };
        set({ messages: [...get().messages, userMessage] });

        await consumeChatStream(
          apiClient.conversationResumeStream(currentConversation.id, responses),
          chatbotId,
          set,
          get
        );
      },

      clearChatHistory: async (chatbotId: string) => {
        set({ isLoadingMessages: true, chatError: null });
        try {
          await apiClient.clearChatHistory(chatbotId);
          set({ messages: [], isLoadingMessages: false });
        } catch (error: any) {
          set({ chatError: error.error || 'Failed to clear chat history', isLoadingMessages: false });
          throw error;
        }
      },

      // ==================== App config ====================
      appInfo: null,
      loadAppInfo: async () => {
        try {
          set({ appInfo: await apiClient.getAppInfo() });
        } catch {
          /* the UI keeps its defaults */
        }
      },

      // ==================== UI Slice ====================
      sidebarOpen: true,
      error: null,
      isLoading: false,

      toggleSidebar: () => {
        set({ sidebarOpen: !get().sidebarOpen });
      },

      clearError: () => {
        set({ error: null, chatError: null, uploadError: null });
      },

      reset: () => {
        set({
          currentChatbot: null,
          chatbots: [],
          currentConversation: null,
          conversations: [],
          documents: [],
          messages: [],
          isLoading: false,
          isLoadingChatbots: false,
          isLoadingConversations: false,
          isLoadingDocuments: false,
          isLoadingMessages: false,
          isStreaming: false,
          isClarifying: false,
      pendingInterrupt: null,
          progress: [],
          isUploading: false,
          sidebarOpen: true,
          error: null,
          chatError: null,
          uploadError: null,
        });
      },
    }),
    { name: 'chatbot-store' }
  )
);

export default useAppStore;
